import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { IconTrash } from '@rossum/ui/icons/tabler';
import { SvgIcon } from '@rossum/ui/material';
import { GridActionsCellItem, GridColDef } from '@rossum/ui/x-data-grid-pro';
import { isSSOEnable } from '../../../../redux/modules/organization/selectors';
import { OrganizationUser } from '../../../../types/user';
import { Toggle } from '../Toggle';

type UseUsersColumnsProps = {
  onDeleteUserClick: (user: OrganizationUser) => void;
  onToggleUserClick: (user: OrganizationUser) => void;
  isActiveToggleDisabled: (user: OrganizationUser) => boolean;
};

export const useUsersColumns = ({
  onDeleteUserClick,
  onToggleUserClick,
  isActiveToggleDisabled,
}: UseUsersColumnsProps) => {
  const intl = useIntl();

  const isSSOEnabled = useSelector(isSSOEnable);

  const defaultConfig: Partial<GridColDef> = {
    sortable: false,
    pinnable: false,
    hideable: false,
    filterable: false,
  };

  const baseColumns: GridColDef<OrganizationUser>[] = R.pipe<
    (GridColDef<OrganizationUser> | undefined)[],
    GridColDef<OrganizationUser>[]
  >(
    [
      {
        field: 'active',
        type: 'actions',
        width: 50,
        getActions: ({ row }) => [
          <div
            key={`active-${row.id}`}
            role="presentation"
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Toggle
              disabled={isActiveToggleDisabled(row)}
              active={row.isActive}
              onToggle={() => onToggleUserClick(row)}
              isUserOrgGroupAdmin={row.role === 'organization_group_admin'}
            />
          </div>,
        ],
      },
      {
        field: 'name',
        headerName: intl.formatMessage({
          id: 'containers.settings.users.addUser.name.label',
        }),
        width: 200,
      },
      {
        field: 'username',
        headerName: intl.formatMessage({
          id: 'containers.settings.users.addUser.email.label',
        }),
        width: 200,
      },
      {
        field: 'role',
        headerName: intl.formatMessage({
          id: 'containers.settings.users.addUser.role.label',
        }),
        width: 200,
        valueGetter: (_, row) =>
          intl.formatMessage({
            id: `containers.settings.users.roles.${row.role}`,
          }),
      },
      isSSOEnabled
        ? {
            field: 'authType',
            headerName: intl.formatMessage({
              id: 'containers.settings.users.addUser.authType',
            }),
            width: 200,
            valueGetter: (_, row) =>
              row.authType
                ? intl.formatMessage({
                    id: `containers.settings.users.authType.${row.authType}`,
                  })
                : '',
          }
        : undefined,
      {
        field: 'workspacesCount',
        headerName: '',
        width: 200,
        valueGetter: (_, row) =>
          intl.formatMessage(
            {
              id: 'containers.settings.users.workspaces',
            },
            { count: row.workspacesCount }
          ),
      },
      {
        field: 'queuesCount',
        headerName: '',
        width: 200,
        valueGetter: (_, row) =>
          intl.formatMessage(
            {
              id: 'containers.settings.users.queues',
            },
            { count: row.queuesCount }
          ),
      },
      {
        field: 'actions',
        type: 'actions',
        width: 50,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key={`actions-${row.id}`}
            data-cy={`delete-user-${row.id}`}
            icon={
              <SvgIcon fontSize="large">
                <IconTrash />
              </SvgIcon>
            }
            onClick={() => onDeleteUserClick(row)}
            label="Delete"
          />,
        ],
      },
    ],
    R.filter(R.isTruthy)
  );

  const columns: GridColDef[] = baseColumns.map(config => ({
    ...defaultConfig,
    ...config,
  }));

  return columns;
};
